import React, { useState, useEffect } from 'react';
import {
    Container,
    Table,
    Card,
    Row,
    Col,
    Form,
    Button,
    Badge,
    Accordion,
    Alert,
    Pagination,
    Modal,
    Spinner
} from 'react-bootstrap';
import app from '../login/firebase.js';
import axios from 'axios';
import NavigationBar from "../components/nav";

const AuditTrailDashboard = () => {
    const [auditLogs, setAuditLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({
        user_id: '',
        action: '',
        table_name: '',
        date_from: '',
        date_to: '',
        page: 1,
        limit: 25
    });
    const [totalPages, setTotalPages] = useState(1);
    const [selectedLog, setSelectedLog] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);

    useEffect(() => {
        fetchAuditLogs();
    }, [filters]);

    const fetchAuditLogs = async () => {
        setLoading(true);
        try {
            const token = await app.auth().currentUser.getIdToken(true);
            const response = await axios.get(process.env.REACT_APP_audit_log, {
                params: filters,
                headers: {
                    Authorization: token
                }
            });

            setAuditLogs(response.data.logs || []);
            setTotalPages(response.data.total_pages || 1);
        } catch (error) {
            console.error('Error fetching audit logs:', error);
        } finally {
            setLoading(false);
        }
    };

    const getActionBadgeVariant = (action) => {
        switch (action) {
            case 'CREATE': return 'success';
            case 'UPDATE': return 'primary';
            case 'DELETE': return 'danger';
            case 'VIEW': return 'info';
            case 'LOGIN': return 'secondary';
            default: return 'light';
        }
    };

    const formatTimestamp = (timestamp) => {
        return new Date(timestamp).toLocaleString();
    };

    // Enhanced field name formatting
    const formatFieldName = (fieldName) => {
        return fieldName
            .replace(/[._]/g, ' ')
            .replace(/([A-Z])/g, ' $1')
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ')
            .trim();
    };

    // Enhanced value formatting
    const formatValue = (value) => {
        if (value === null || value === undefined) {
            return <span className="text-muted fst-italic">Not set</span>;
        }
        if (typeof value === 'boolean') {
            return value ?
                <span className="text-success">✓ Yes</span> :
                <span className="text-danger">✗ No</span>;
        }
        if (typeof value === 'string' && value.length === 0) {
            return <span className="text-muted fst-italic">Empty</span>;
        }
        if (typeof value === 'object') {
            return <code className="bg-light px-2 py-1 rounded small">{JSON.stringify(value)}</code>;
        }
        return <span>{String(value)}</span>;
    };

    const renderChangedFields = (changedFields) => {
        if (!changedFields || Object.keys(changedFields).length === 0) {
            return <span className="text-muted">No field changes</span>;
        }
        return (
            <div>
                {Object.entries(changedFields).map(([field, change]) => (
                    <div key={field} className="mb-3 p-3 border rounded bg-light">
                        <div className="fw-bold text-primary mb-2">
                            {formatFieldName(field)}
                        </div>
                        <div className="row">
                            <div className="col-md-6">
                                <small className="text-muted d-block">Previous:</small>
                                <div className="p-2 bg-white border rounded">
                                    {formatValue(change.old)}
                                </div>
                            </div>
                            <div className="col-md-6">
                                <small className="text-muted d-block">Updated to:</small>
                                <div className="p-2 bg-white border rounded border-success">
                                    {formatValue(change.new)}
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    };
    const [flushing, setFlushing]           = useState(false);   // spinner state
    const [showFlushConfirm, setShowFlushConfirm] = useState(false);
    const [showFlushResult, setShowFlushResult]   = useState(false);
    const [flushResultMsg, setFlushResultMsg]     = useState('');
    const flushLogs = async () => {
        setFlushing(true);
        try {
            const token = await app.auth().currentUser.getIdToken(true);
            await axios.delete(process.env.REACT_APP_audit_flush, {
                headers: { Authorization: token }
            });

            // refresh list & show success
            fetchAuditLogs();
            setFlushResultMsg('Audit logs flushed successfully.');
        } catch (err) {
            console.error('Error flushing audit logs:', err);
            setFlushResultMsg(
                err.response?.status === 403
                    ? 'Only a super user can flush logs.'
                    : 'Failed to flush audit logs.'
            );
        } finally {
            setFlushing(false);
            setShowFlushConfirm(false);   // hide confirm dialog
            setShowFlushResult(true);     // show result dialog
        }
    };


    const renderCompactChanges = (changedFields) => {
        if (!changedFields || Object.keys(changedFields).length === 0) {
            return <span className="text-muted">No changes</span>;
        }
        const fieldCount = Object.keys(changedFields).length;
        const firstField = Object.keys(changedFields)[0];
        if (fieldCount === 1) {
            const change = changedFields[firstField];
            return (
                <div>
                    <div className="fw-bold text-primary small">
                        {formatFieldName(firstField)}
                    </div>
                    <div className="d-flex align-items-center small">
                        <span className="text-muted me-2">{formatValue(change.old)}</span>
                        <span className="text-muted me-2">→</span>
                        <span className="text-success fw-bold">{formatValue(change.new)}</span>
                    </div>
                </div>
            );
        }
        return (
            <small>
                <strong className="text-primary">{formatFieldName(firstField)}</strong> and {fieldCount - 1} other field{fieldCount > 2 ? 's' : ''}
            </small>
        );
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value,
            page: 1
        }));
    };

    const clearFilters = () => {
        setFilters({
            user_id: '',
            action: '',
            table_name: '',
            date_from: '',
            date_to: '',
            page: 1,
            limit: 25
        });
    };

    const showLogDetails = (log) => {
        setSelectedLog(log);
        setShowDetailModal(true);
    };

    return (
        <div>
            <NavigationBar/>
            <Container className="py-4">
                <h2 className="mb-4">Audit Trail Dashboard</h2>

                <Card className="mb-4 shadow-sm">
                    <Card.Header className="bg-primary text-white">
                        <h5 className="mb-0">Filters</h5>
                    </Card.Header>
                    <Card.Body>
                        <Row>
                            <Col md={3}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Action</Form.Label>
                                    <Form.Control
                                        as="select"
                                        name="action"
                                        value={filters.action}
                                        onChange={handleFilterChange}
                                    >
                                        <option value="">All Actions</option>
                                        <option value="CREATE">Create</option>
                                        <option value="UPDATE">Update</option>
                                        <option value="DELETE">Delete</option>
                                        <option value="VIEW">View</option>
                                        <option value="LOGIN">Login</option>
                                    </Form.Control>
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Table</Form.Label>
                                    <Form.Control
                                        as="select"
                                        name="table_name"
                                        value={filters.table_name}
                                        onChange={handleFilterChange}
                                    >
                                        <option value="">All Tables</option>
                                        <option value="personal_info">Personal Info</option>
                                        <option value="visits">Visits</option>
                                        <option value="patients_list">Patients List</option>
                                        <option value="medical_history">Medical History</option>
                                    </Form.Control>
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group className="mb-3">
                                    <Form.Label>From Date</Form.Label>
                                    <Form.Control
                                        type="datetime-local"
                                        name="date_from"
                                        value={filters.date_from}
                                        onChange={handleFilterChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group className="mb-3">
                                    <Form.Label>To Date</Form.Label>
                                    <Form.Control
                                        type="datetime-local"
                                        name="date_to"
                                        value={filters.date_to}
                                        onChange={handleFilterChange}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>User Email</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="user_id"
                                        value={filters.user_id}
                                        onChange={handleFilterChange}
                                        placeholder="Filter by user email..."
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6} className="d-flex align-items-end">
                                <Button
                                    variant="outline-secondary"
                                    onClick={clearFilters}
                                    className="me-2"
                                >
                                    Clear Filters
                                </Button>


                            </Col>
                            <Col  md={6} className="d-flex align-items-end">
                                <Button
                                    variant="danger"
                                    onClick={() => setShowFlushConfirm(true)}
                                    disabled={flushing}
                                    className="ms-2"
                                    title="Permanently delete audit-trail rows"
                                >
                                    {flushing ? (
                                        <>
                                            <Spinner
                                                as="span"
                                                animation="border"
                                                size="sm"
                                                className="me-1"
                                            />
                                            Flushing…
                                        </>
                                    ) : (
                                        'Flush Logs'
                                    )}
                                </Button>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

                <Card className="shadow-sm">
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5>Audit Logs</h5>
                            {loading && <Spinner animation="border" size="sm" />}
                        </div>

                        {loading ? (
                            <div className="text-center py-4">
                                <Spinner animation="border" />
                                <div>Loading audit logs...</div>
                            </div>
                        ) : (
                            <>
                                <Table responsive striped hover>
                                    <thead className="table-dark">
                                    <tr>
                                        <th>Timestamp</th>
                                        <th>User</th>
                                        <th>Action</th>
                                        <th>Table</th>
                                        <th>Record ID</th>
                                        <th>Changes</th>
                                        <th>IP Address</th>
                                        <th>Actions</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {auditLogs.map((log) => (
                                        <tr key={log.id}>
                                            <td><small>{formatTimestamp(log.timestamp)}</small></td>
                                            <td>
                                                <div className="d-flex align-items-center">
                                                    <div className="bg-primary rounded-circle text-white d-flex align-items-center justify-content-center me-2"
                                                         style={{width: '24px', height: '24px', fontSize: '10px'}}>
                                                        {(log.user_email || 'U').charAt(0).toUpperCase()}
                                                    </div>
                                                    <small>{log.user_email || 'Unknown'}</small>
                                                </div>
                                            </td>
                                            <td><Badge bg={getActionBadgeVariant(log.action)}>{log.action}</Badge></td>
                                            <td><small>{log.table_name || '-'}</small></td>
                                            <td>
                                                <code className="bg-light px-2 py-1 rounded small">
                                                    {log.record_id || '-'}
                                                </code>
                                            </td>
                                            <td style={{ maxWidth: '200px' }}>{renderCompactChanges(log.changed_fields)}</td>
                                            <td><small>{log.ip_address || '-'}</small></td>
                                            <td>
                                                <Button variant="outline-primary" size="sm" onClick={() => showLogDetails(log)}>
                                                    Details
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </Table>

                                {auditLogs.length === 0 && (
                                    <Alert variant="info" className="text-center">
                                        No audit logs found matching the current filters.
                                    </Alert>
                                )}
                            </>
                        )}
                    </Card.Body>
                </Card>

                {selectedLog && (
                    <Modal
                        show={showDetailModal}
                        onHide={() => setShowDetailModal(false)}
                        size="lg"
                        centered
                    >
                        <Modal.Header closeButton className="bg-primary text-white">
                            <Modal.Title>Audit Log Details</Modal.Title>
                        </Modal.Header>

                        <Modal.Body>
                            <Row className="mb-4">
                                <Col md={6}>
                                    <div className="border rounded p-3 h-100">
                                        <h6 className="text-primary mb-3">Basic Information</h6>
                                        <p><strong>User:</strong> {selectedLog.user_email || 'Unknown'}</p>
                                        <p><strong>Action:</strong> <Badge bg={getActionBadgeVariant(selectedLog.action)}>{selectedLog.action}</Badge></p>
                                        <p><strong>Table:</strong> {selectedLog.table_name}</p>
                                        <p><strong>Record ID:</strong> <code className="bg-light px-2 py-1 rounded">{selectedLog.record_id}</code></p>
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="border rounded p-3 h-100">
                                        <h6 className="text-primary mb-3">Session Details</h6>
                                        <p><strong>Timestamp:</strong> {new Date(selectedLog.timestamp).toLocaleString()}</p>
                                        <p><strong>IP Address:</strong> <code className="bg-light px-2 py-1 rounded">{selectedLog.ip_address || 'Not recorded'}</code></p>
                                    </div>
                                </Col>
                            </Row>

                            <div className="border rounded p-3">
                                <h6 className="text-primary mb-3">Changed Fields</h6>
                                {renderChangedFields(selectedLog.changed_fields)}
                            </div>
                        </Modal.Body>
                    </Modal>
                )}
                {/* Confirm-flush dialog */}
                <Modal
                    show={showFlushConfirm}
                    onHide={() => setShowFlushConfirm(false)}
                    centered
                >
                    <Modal.Header closeButton>
                        <Modal.Title>Confirm Flush</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        This will <strong>permanently delete</strong> all audit-trail rows.
                        Are you sure you want to continue?
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowFlushConfirm(false)}>
                            Cancel
                        </Button>
                        <Button variant="danger" onClick={flushLogs} disabled={flushing}>
                            {flushing ? 'Working…' : 'Yes, Flush'}
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* Result dialog */}
                <Modal
                    show={showFlushResult}
                    onHide={() => setShowFlushResult(false)}
                    centered
                >
                    <Modal.Header closeButton>
                        <Modal.Title>Flush Logs</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>{flushResultMsg}</Modal.Body>
                    <Modal.Footer>
                        <Button variant="primary" onClick={() => setShowFlushResult(false)}>
                            OK
                        </Button>
                    </Modal.Footer>
                </Modal>

            </Container>
        </div>
    );
};

export default AuditTrailDashboard;